import React, { useState, useEffect, useRef } from 'react'
import BaseModal from '@components/common/BaseModal/BaseModal.component'
import InputField from '@components/common/InputField/InputField.component'
import Button from '@components/common/Button/Button.component'
import './RenameFolderModal.css'

interface RenameFolderModalProps {
  show: boolean
  onHide: () => void
  currentName: string
  onRename: (newName: string) => void
  isLoading?: boolean
}

const RenameFolderModal: React.FC<RenameFolderModalProps> = ({
  show,
  onHide,
  currentName,
  onRename,
  isLoading = false,
}) => {
  const [folderName, setFolderName] = useState(currentName)
  const [isValid, setIsValid] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setFolderName(currentName)
  }, [currentName])

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus()
    }
  }, [show])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    setFolderName(value)
    setIsValid(value.trim().length > 0)
  }

  const handleSave = (): void => {
    if (isValid && folderName.trim() !== currentName) {
      onRename(folderName.trim())
      onHide()
    }
  }

  const handleCancel = (): void => {
    setFolderName(currentName)
    setIsValid(true)
    onHide()
  }

  const modalContent = (
    <div className="rename-folder-form">
      <InputField
        label="Folder name"
        type="text"
        value={folderName}
        onChange={handleInputChange}
        placeholder="Enter folder name"
        disabled={isLoading}
        ref={inputRef}
      />
    </div>
  )

  const modalFooter = (
    <div className="rename-folder-actions">
      <Button
        className="rename-folder-cancel"
        onClick={handleCancel}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Button
        className="rename-folder-save"
        onClick={handleSave}
        disabled={!isValid || folderName.trim() === currentName || isLoading}
      >
        {isLoading ? 'Saving...' : 'Save'}
      </Button>
    </div>
  )

  return (
    <BaseModal
      show={show}
      handleClose={handleCancel}
      title="Rename folder"
      footer={modalFooter}
      className="rename-folder-modal"
      size="sm"
    >
      {modalContent}
    </BaseModal>
  )
}

export default RenameFolderModal
